import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
  toast,
  type IconName,
} from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { datedFilename, saveBlob } from "../shared/download";
import { useAgentList } from "../shared/use-engage-data";
import type { IntegrationKey } from "./lead-types";
import { LeadRoutingButton } from "./lead-routing-dialog";
import { useSyncResultToast } from "./sync-result-toast";
import { useExportLeads, useIntegrations, useSyncLeads } from "./use-leads";

const CRM: readonly { key: IntegrationKey; icon: IconName }[] = [
  { key: "google_sheets", icon: "file-spreadsheet" },
  { key: "hubspot", icon: "target" },
  { key: "zoho", icon: "layers" },
];

interface LeadsActionsProps {
  stageId: string | null;
  canExport: boolean;
  canEdit: boolean;
  canEditStages: boolean;
  onEditStages: () => void;
}

function CrmMenu({ integrationsHref }: { integrationsHref: string | null }) {
  const { t } = useTranslation();
  const integrations = useIntegrations();
  const sync = useSyncLeads();
  const notifySync = useSyncResultToast();
  const onSync = (provider: IntegrationKey) => {
    sync.mutate(provider, {
      onSuccess: (result) => { notifySync(provider, result); },
      onError: () => toast.error(t("engage.leads.crm.failed", { provider: t(`engage.leads.crm.${provider}`) })),
    });
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button loading={sync.isPending}>
          <Icon name="refresh-cw" />
          {t("engage.leads.syncToCrm")}
          <Icon name="chevron-down" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {CRM.map(({ key, icon }) => {
          const integration = integrations.data?.find((i) => i.key === key);
          const connected = integration?.status === "connected";
          const needsReconnect = integration?.status === "needs_reconnect";
          const comingSoon = integration !== undefined && !integration.available;
          return (
            <DropdownMenuItem key={key} className="items-start gap-3" disabled={comingSoon} onSelect={() => { onSync(key); }}>
              <Icon name={icon} className={cn("mt-0.5 size-4", connected ? "text-success" : needsReconnect ? "text-warning" : "text-fg-subtle")} />
              <span className="flex-1">
                <span className="block text-sm font-semibold">{t(`engage.leads.crm.${key}`)}</span>
                <span className="block text-xs text-fg-subtle">{connected ? t("engage.leads.crm.connected") : needsReconnect ? t("engage.leads.crm.reconnectNeeded") : t("engage.leads.crm.notConnected")}</span>
              </span>
              {connected ? <Icon name="check" className="mt-0.5 size-4 text-primary-ink" /> : null}
              {comingSoon ? <Badge tone="warning">{t("common.comingSoon")}</Badge> : null}
            </DropdownMenuItem>
          );
        })}
        {integrationsHref ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <AppLink href={integrationsHref}>
                <Icon name="webhook" className="size-4" />
                {t("engage.leads.crm.other")}
              </AppLink>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Header actions: lead form fields, lead routing, pipeline stages, Export CSV (leads.export), Sync to CRM. */
export function LeadsActions({ stageId, canExport, canEdit, canEditStages, onEditStages }: LeadsActionsProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const errorMessage = useErrorMessage();
  const agents = useAgentList();
  const exportLeads = useExportLeads();
  const firstAgent = agents.data?.[0];
  const onExport = () => {
    exportLeads.mutate(stageId, {
      onSuccess: (blob) => {
        const file = datedFilename(`${summary.slug}-leads`, "csv");
        saveBlob(blob, file);
        toast.success(t("engage.leads.exported", { file }));
      },
      onError: (error) => toast.error(errorMessage(error)),
    });
  };
  return (
    <>
      {firstAgent ? (
        <Button asChild variant="ghost">
          <AppLink href={consoleRoutes.widgetStudio(summary.slug, firstAgent.id)}>
            <Icon name="sliders-horizontal" />
            {t("engage.leads.leadFormFields")}
          </AppLink>
        </Button>
      ) : null}
      <LeadRoutingButton canEdit={canEdit} />
      {canEditStages ? (
        <Button variant="ghost" onClick={onEditStages}>
          <Icon name="list-checks" />
          {t("engage.leads.editStages")}
        </Button>
      ) : null}
      {canExport ? (
        <Button variant="secondary" loading={exportLeads.isPending} onClick={onExport}>
          <Icon name="download" />
          {t("engage.leads.exportCsv")}
        </Button>
      ) : null}
      {canEdit ? <CrmMenu integrationsHref={firstAgent ? consoleRoutes.channels(summary.slug, firstAgent.id) : null} /> : null}
    </>
  );
}
